import Vue from 'vue';
import Vuex from 'vuex';

import {
  SET_APP_CONFIG,
  SET_USER_PERMISSIONS,
  SET_LOCALES,
  SET_LANGUAGES,
  SET_CURRENT_LANGUAGE_ID,
  SET_CHARGING,
  SET_LEVELS,
  SET_CURRENT_LEVEL_ID,
  SET_AMOUNTS,
  SET_TEACHER_ONLINE,
  SET_TERMS,
  INITIAL_DATA_RECEIVED,
  SECONDARY_DATA_RECEIVED
} from '@/store/mutations.js';
import modules from './modules/index.js';
import notify from '@/js/helpers/notify.js';
import apiRequest from '@/js/helpers/apiRequest.js';

Vue.use(Vuex);

export default new Vuex.Store({
  modules,

  state: {
    appConfig: {},
    permissions: {
      microphone: '',
      camera: ''
    },
    languages: [],
    locales: [],
    levels: [],
    charging: 0,
    amounts: [],
    teachersOnline: {
      avatars: [],
      count: 0
    },
    terms: [],
    selectLanguageId: null,
    selectLevel: null,
    initialStateLoad: false,
    secondaryStateLoad: true
  },

  mutations: {
    [SET_APP_CONFIG]: (state, appConfig) => {
      state.appConfig = appConfig;
    },

    [SET_USER_PERMISSIONS]: (state, { type, result }) => {
      state.permissions[type] = result;
    },

    [SET_LOCALES]: (state, locales) => {
      state.locales = locales;
    },

    [SET_LANGUAGES](state, languages) {
      state.languages = languages;
    },

    [SET_CURRENT_LANGUAGE_ID]: (state, id) => {
      state.selectLanguageId = id;
    },

    [SET_LEVELS](state, levels) {
      state.levels = levels;
    },

    [SET_CURRENT_LEVEL_ID]: (state, id) => {
      state.selectLevel = id;
    },

    [SET_CHARGING](state, charging) {
      state.charging = charging;
    },

    [SET_AMOUNTS](state, { amounts }) {
      state.amounts = amounts;
    },

    [SET_TEACHER_ONLINE](state, payload) {
      state.teachersOnline = payload;
    },

    [SET_TERMS](state, terms) {
      state.terms = terms;
    },

    [INITIAL_DATA_RECEIVED](state) {
      state.initialStateLoad = !state.initialStateLoad;
    },

    [SECONDARY_DATA_RECEIVED](state) {
      state.secondaryStateLoad = !state.secondaryStateLoad;
    }
  },

  actions: {
    async getAppConfig({ commit }) {
      try {
        const res = await apiRequest('config', 'GET', null);

        if (res.error) {
          throw { customeError: res.error };
        }

        commit(SET_APP_CONFIG, res.success);
      } catch (error) {
        if (error.customeError) {
          notify({ text: error.customeError });
        } else {
          console.error('ERROR: config:', error);
        }
      }
    },

    async getLanguages({ commit }) {
      try {
        const res = await apiRequest('languages', 'GET', null);

        if (res.error) {
          throw { customeError: res.error };
        }

        const languages = res.success.languages.map(({ id, name, image }) => ({
          id,
          name,
          image
        }));

        commit(SET_LANGUAGES, languages);
        commit(SET_CURRENT_LANGUAGE_ID, languages[0].id);
      } catch (error) {
        if (error.customeError) {
          notify({ text: error.customeError });
        } else {
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      }
    },

    async getLevels({ commit }) {
      try {
        const res = await apiRequest('levels', 'GET', null);

        if (res.error) {
          throw { customeError: res.error };
        }

        const levels = res.success.levels
          .map(({ id, name, price, image, video }) => ({
            id,
            name,
            price,
            image,
            isVideo: !!video
          }))
          .sort((a, b) => a.price - b.price);

        commit(SET_LEVELS, levels);
        commit(SET_CHARGING, res.success.charging);
        commit(SET_CURRENT_LEVEL_ID, levels[0].id);
      } catch (error) {
        if (error.customeError) {
          notify({ text: error.customeError });
        } else {
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      }
    },

    async getAmounts({ commit }) {
      try {
        const res = await apiRequest('pay/amounts', 'GET', null, true);

        if (res.error) {
          throw { customeError: res.error };
        }

        commit(SET_AMOUNTS, { amounts: res.success });
      } catch (error) {
        if (error.customeError) {
          notify({ text: error.customeError });
        } else {
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      }
    },

    async getTeachersOnline({ commit }) {
      try {
        const res = await apiRequest('teachers/online', 'GET', null, false);

        if (res.error) {
          throw { customeError: res.error };
        }

        commit(SET_TEACHER_ONLINE, {
          avatars: res.success.teachers,
          count: res.success.teachers_count
        });
      } catch (error) {
        if (error.customeError) {
          notify({ text: error.customeError });
        } else {
          console.error(error);
          notify({ text: this.$t(`notify.something_went_wrong`) });
        }
      }
    },

    getTerms: async ({ commit }) => {
      const res = await apiRequest('terms', 'GET', null, false);

      if (res.error) {
        notify({ text: res.error });
        throw { customeError: res.error };
      }

      commit(SET_TERMS, res.success);
    }
  }
});
